import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import MenuBar from '../../components/menuBar/menuBar';
import PageFooter from '../../components/pageFooter/pageFooter';
import styles from '../../publicAsset/termsOfServicePage.module.scss';
import ContactConstant from '../../utils/Constant/ContactConstant';

const SECTIONS = [
    { id: 'part1', label: 'Part 1: Service Cancellation and Refund Procedure' },
    { id: 'section-1-1', label: '1.1 Refund Eligibility' },
    { id: 'section-1-2', label: '1.2 Non-Eligibility for Refund' },
    { id: 'section-1-3', label: '1.3 Tutor Session Cancellation and Forfeiture' },
    { id: 'section-1-4', label: '1.4 Refund Procedure Steps' },
    { id: 'part2', label: 'Part 2: Complaint Handling Procedure' },
    { id: 'section-2-1', label: '2.1 Channels for Submitting Complaints' },
    { id: 'section-2-2', label: '2.2 Complaint Resolution Process' },
    { id: 'section-2-3', label: '2.3 Special Handling for Tutor Quality Complaints' },
    { id: 'section-2-4', label: '2.4 Final Dispute Resolution' },
];

const RefundComplaintPolicyPage = () => {
    const [activeSection, setActiveSection] = useState('overview');

    // Scroll-driven fixed sidebar — position:sticky breaks when html/body has overflow-x:hidden
    const heroRef = useRef(null);
    const sidebarWrapperRef = useRef(null);
    const sidebarRef = useRef(null);
    const containerRef = useRef(null);
    const [isFixed, setIsFixed] = useState(false);
    const [fixedStyle, setFixedStyle] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const update = () => {
            if (!heroRef.current || !sidebarWrapperRef.current || !containerRef.current || !sidebarRef.current) return;
            const heroBottom = heroRef.current.getBoundingClientRect().bottom;
            const wrapperRect = sidebarWrapperRef.current.getBoundingClientRect();
            const containerBottom = containerRef.current.getBoundingClientRect().bottom;
            const sidebarHeight = sidebarRef.current.offsetHeight;
            const pastHero = heroBottom <= 88;
            const beforeFooter = containerBottom > sidebarHeight + 88 + 12;
            if (pastHero && beforeFooter) {
                setIsFixed(true);
                setFixedStyle({
                    position: 'fixed',
                    top: 88,
                    left: wrapperRect.left,
                    width: wrapperRect.width,
                });
            } else {
                setIsFixed(false);
                setFixedStyle({});
            }
        };
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveSection(entry.target.id);
                });
            },
            { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
        );
        SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 90;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    return (
        <div className={styles.page}>
            <Helmet>
                <title>Refund & Complaint Policy | Prepal</title>
                <meta name="description" content="Read Prepal's Refund and Complaint Policy to understand our refund eligibility, process, and how we handle complaints." />
                <meta name="keywords" content="Prepal refund policy, complaint policy, subscription refund, Prepal support" />
                <meta name="author" content="Prepal Team" />
            </Helmet>

            <MenuBar />

            {/* Hero Banner */}
            <div className={styles.hero} ref={heroRef}>
                <div className={styles.heroContent}>
                    <p className={styles.heroEyebrow}>Legal</p>
                    <h1 className={styles.heroTitle}>Refund & Complaint Policy</h1>
                    <p className={styles.heroSubtitle}>
                        This Policy details the standardized procedures prepal uses to process service cancellations, issue refunds, and manage customer complaints.
                    </p>
                    <p className={styles.heroMeta}>Effective Date: <strong>March 5, 2026</strong></p>
                </div>
            </div>

            {/* Main Layout */}
            <div className={styles.container} ref={containerRef}>
                {/* Wrapper always holds flex space; inner aside goes fixed via JS */}
                <div ref={sidebarWrapperRef} className={styles.sidebarWrapper}>
                    <aside
                        ref={sidebarRef}
                        className={`${styles.sidebar} ${isFixed ? styles.sidebarFixed : ''}`}
                        style={fixedStyle}
                    >
                        <div className={styles.tocCard}>
                            <p className={styles.tocHeading}>Table of Contents</p>
                            <nav>
                                <ul className={styles.tocList}>
                                    {SECTIONS.map(({ id, label }) => (
                                        <li key={id}>
                                            <button
                                                className={`${styles.tocItem} ${activeSection === id ? styles.tocItemActive : ''}`}
                                                onClick={() => scrollToSection(id)}
                                            >
                                                {label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>
                    </aside>
                </div>

                {/* Content */}
                <main className={styles.content}>

                    <section className={styles.section}>
                        <p className={styles.text}>
                            This Policy details the standardized procedures prepal uses to process service cancellations, issue refunds, and manage customer complaints. This document is a supplement to and forms part of the prepal Terms of Service.
                        </p>
                    </section>

                    <section id="part1" className={styles.section}>
                        <h2 className={styles.sectionTitle}>Part 1: Service Cancellation and Refund Procedure</h2>
                        <p className={styles.text}>
                            Our refund procedures comply with Vietnamese Consumer Protection Laws, ensuring fair and prompt resolution.
                        </p>
                    </section>

                    <section id="section-1-1" className={styles.section}>
                        <h2 className={styles.sectionTitle}>1.1 Refund Eligibility</h2>

                        <h3 className={styles.subSectionTitle}>A. Digital Subscription — Early Access (Current)</h3>
                        <p className={styles.text}>
                            <strong>14-Day Money-Back Guarantee:</strong> A full refund is available within 14 days of purchase if the user is generally unsatisfied with the product or service, provided the user has not completed the Diagnostic Test (when available) or accessed more than 10% of the adaptive practice content.
                        </p>

                        <h3 className={styles.subSectionTitle}>A. Digital Subscription — Full Release (Future)</h3>
                        <p className={styles.text}>
                            <strong>Technical Issue Refund:</strong> Full refund is only issued if the user can provide a valid technical support ticket proving a critical and persistent infrastructure failure on our side that prevents them from using the service. No refunds for general dissatisfaction after the transition to subscription pricing.
                        </p>

                        <h3 className={styles.subSectionTitle}>B. Tutor Session Packages (Future)</h3>
                        <p className={styles.text}>
                            Full refund available at any time for sessions that have NOT been scheduled. See Section 1.3 for cancellation rules on scheduled sessions.
                        </p>

                        <h3 className={styles.subSectionTitle}>C. Product Defect</h3>
                        <p className={styles.text}>
                            Full refund or service credit provided if a critical defect (bug) prevents access to a core service feature for more than 48 consecutive hours, validated by the prepal technical team.
                        </p>
                    </section>

                    <section id="section-1-2" className={styles.section}>
                        <h2 className={styles.sectionTitle}>1.2 Non-Eligibility for Refund</h2>
                        <ol className={styles.orderedList}>
                            <li><strong>Early Access:</strong> No refund is available after the 14-day window expires OR if usage exceeds the 10% content threshold.</li>
                            <li><strong>Full Release:</strong> No refund is issued for reasons other than critical, service-hindering technical failure validated by the prepal Support team.</li>
                            <li><strong>General:</strong> Normal service downtime for maintenance (which we will announce in advance) does not qualify for a refund.</li>
                        </ol>
                    </section>

                    <section id="section-1-3" className={styles.section}>
                        <h2 className={styles.sectionTitle}>1.3 Tutor Session Cancellation and Forfeiture (Future)</h2>
                        <ul className={styles.list}>
                            <li><strong>Standard Cancellation (Full Refund):</strong> Users must cancel a booked session at least 48 hours before the scheduled start time to retain the full session credit.</li>
                            <li><strong>Late Cancellation (50% Refund):</strong> If a session is cancelled less than 48 hours before the scheduled start time, the User will receive a 50% refund. The remaining 50% is retained by prepal to compensate the tutor.</li>
                        </ul>
                    </section>

                    <section id="section-1-4" className={styles.section}>
                        <h2 className={styles.sectionTitle}>1.4 Refund Procedure Steps</h2>
                        <ol className={styles.orderedList}>
                            <li><strong>Request Submission:</strong> The User must submit a formal request via email to {ContactConstant.INFO_EMAIL} or through the billing section of their account.</li>
                            <li><strong>Review and Calculation:</strong> The prepal team will review the User's account usage and calculate the eligible refund amount based on Section 1.1. This review will be completed within 2-3 working days.</li>
                            <li><strong>Approval and Tax Adjustment:</strong> Upon approval, prepal will proceed with the refund and initiate correction of the transaction record by issuing an Adjusted Electronic Invoice (Hóa đơn điều chỉnh) to comply with Vietnamese tax regulations.</li>
                            <li><strong>Processing and Timeline:</strong> The refund will be processed through Paddle (our payment processor) within 2-3 working days of the approval date. The User is advised that banks may take an additional 3-5 working days for the funds to reflect in their account.</li>
                            <li><strong>Service Deactivation:</strong> The associated service access will be revoked upon the initiation of the refund process.</li>
                        </ol>
                    </section>

                    <section id="part2" className={styles.section}>
                        <h2 className={styles.sectionTitle}>Part 2: Complaint Handling Procedure</h2>
                        <p className={styles.text}>
                            We are committed to resolving all customer complaints fairly, transparently, and quickly, with an emphasis on negotiation and mediation as required by law.
                        </p>
                    </section>

                    <section id="section-2-1" className={styles.section}>
                        <h2 className={styles.sectionTitle}>2.1 Channels for Submitting Complaints</h2>
                        <p className={styles.text}>
                            Complaints must be submitted in writing to ensure accurate tracking and detailed investigation.
                        </p>
                        <ul className={styles.unorderedList}>
                            <li><strong>Email:</strong> <a href={`mailto:${ContactConstant.INFO_EMAIL}`} className={styles.contactValue}>{ContactConstant.INFO_EMAIL}</a></li>
                            <li><strong>Website:</strong> Through the support section at studyprepal.com</li>
                        </ul>
                    </section>

                    <section id="section-2-2" className={styles.section}>
                        <h2 className={styles.sectionTitle}>2.2 Complaint Resolution Process</h2>
                        <table className={styles.policyTable}>
                            <thead>
                                <tr>
                                    <th>Step</th>
                                    <th>Action</th>
                                    <th>Timeline</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>1. Acknowledgement</strong></td>
                                    <td>Support Team logs the complaint, assigns a unique Complaint ID, and sends an acknowledgment email.</td>
                                    <td>Within 2 business hours</td>
                                </tr>
                                <tr>
                                    <td><strong>2. Initial Triage</strong></td>
                                    <td>Support Lead assesses the complaint type (Technical, Billing, Tutor Quality) and assigns to the relevant team.</td>
                                    <td>Within 2 working days</td>
                                </tr>
                                <tr>
                                    <td><strong>3. Investigation</strong></td>
                                    <td>Relevant team gathers system logs, transaction data, or session recordings/reports.</td>
                                    <td>Within 2 working days</td>
                                </tr>
                                <tr>
                                    <td><strong>4. Resolution Proposal</strong></td>
                                    <td>prepal drafts a resolution (system fix, refund, service credit, or replacement session). Prioritizes mediation.</td>
                                    <td>Within 3-4 working days from complaint date</td>
                                </tr>
                                <tr>
                                    <td><strong>5. Final Resolution</strong></td>
                                    <td>If the User accepts, the resolution is executed and the case is formally closed and logged.</td>
                                    <td>Within 2 working days of acceptance</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>

                    <section id="section-2-3" className={styles.section}>
                        <h2 className={styles.sectionTitle}>2.3 Special Handling for Tutor Quality Complaints (Future)</h2>
                        <ul className={styles.unorderedList}>
                            <li><strong>Verification:</strong> prepal will review the session to determine if the complaint relates to unprofessional conduct, lack of subject knowledge, or persistent technical failure.</li>
                            <li><strong>Resolution:</strong> If validated, the User will be credited the session fee and offered a free replacement session with a different tutor.</li>
                            <li><strong>Tutor Action:</strong> Repeated verified complaints will result in disciplinary action, up to and including removal from the platform.</li>
                        </ul>
                    </section>

                    <section id="section-2-4" className={styles.section}>
                        <h2 className={styles.sectionTitle}>2.4 Final Dispute Resolution</h2>
                        <p className={styles.text}>
                            If prepal and the User cannot reach a mutually acceptable resolution through negotiation and mediation (Steps 1-5) within 30 days, the User has the right to refer the dispute to the competent consumer protection authorities or the courts in Ho Chi Minh City.
                        </p>
                        <p className={`${styles.text} ${styles.textItalic}`}>
                            For all refund requests and complaints, contact: <a href={`mailto:${ContactConstant.INFO_EMAIL}`} className={styles.contactValue}>{ContactConstant.INFO_EMAIL}</a>
                        </p>
                    </section>



                    <div className={styles.bottomBar}>
                        <p>© {new Date().getFullYear()} Prepal Inc. All rights reserved.</p>
                        <div className={styles.bottomLinks}>
                            <Link to="/landing" className={styles.bottomLink}>Home</Link>
                            <Link to="/terms-of-service" className={styles.bottomLink}>Terms of Service</Link>
                            <Link to="/privacy-policy" className={styles.bottomLink}>Privacy Policy</Link>
                        </div>
                    </div>

                </main>
            </div>

            <PageFooter />
        </div>
    );
};

export default RefundComplaintPolicyPage;
